/**
 * CO-225 — shared helpers for the daily AI request quota.
 *
 * The reset is UTC midnight for every tenant. That is NOT midnight for the user: a Riyadh user's
 * allowance comes back at 3am local, a Casablanca user's at 1am. So nothing here ever says
 * "midnight" — the time is always rendered in UTC and labelled as such, and a relative
 * "in about N hours" is offered alongside it because a bare UTC clock time is not something a
 * person can act on.
 *
 * Pure functions only, no React and no i18next import: `requestHandling.ts` calls these from
 * inside an axios interceptor.
 */

/** Shape the server sends on a 429 refusal from EnforceAiQuotaAttribute. */
export interface AiQuotaRefusal {
    error: 'ai_quota_exceeded';
    used: number;
    limit: number;
    resetsAtUtc: string;
}

export const AI_QUOTA_ERROR_CODE = 'ai_quota_exceeded';

/** Burst-limiter refusal (Program.cs OnRejected). Distinct from the daily quota; both are 429. */
export const RATE_LIMITED_ERROR_CODE = 'rate_limited';

/**
 * "00:00 UTC, 17 Aug" — the clock time is forced to the UTC zone and the zone is named, so a
 * reader in any offset can tell it is not their own midnight.
 */
export function formatUtcReset(resetsAtUtc: string | null | undefined, locale?: string): string {
    if (!resetsAtUtc) return '';
    const date = new Date(resetsAtUtc);
    if (Number.isNaN(date.getTime())) return '';
    try {
        const formatted = new Intl.DateTimeFormat(locale || undefined, {
            timeZone: 'UTC',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
            day: 'numeric',
            month: 'short',
        }).format(date);
        return `${formatted} UTC`;
    } catch {
        return `${date.toISOString().slice(11, 16)} UTC`;
    }
}

/** Whole hours until the reset, floored at 1 so it never reads "in 0 hours". */
export function hoursUntilReset(resetsAtUtc: string | null | undefined): number {
    if (!resetsAtUtc) return 0;
    const date = new Date(resetsAtUtc);
    if (Number.isNaN(date.getTime())) return 0;
    const hours = Math.ceil((date.getTime() - Date.now()) / 3_600_000);
    return hours < 1 ? 1 : hours;
}

/**
 * Whether the allowance is nearly gone. Drives the one colour change in the indicator: it stays
 * quiet muted text until it actually matters, so it is information rather than a permanent warning
 * badge the user learns to ignore.
 */
export function isQuotaLow(used: number, limit: number): boolean {
    if (limit <= 0) return false;
    const remaining = limit - used;
    return remaining <= Math.max(1, Math.round(limit * 0.1));
}
